import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";
import { runValidators, validators } from "./validators";

const empty = { name: "", role: "", linkedin_url: "", photo_id: null, order: 0, status: "active" };

export default function TeamManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => setItems((await adminApi.get("/admin/team")).data);
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.role) return toast.error("Name and role required");
    const linkedinErr = runValidators(form.linkedin_url || "", [validators.url]);
    if (linkedinErr) return toast.error(`LinkedIn URL: ${linkedinErr}`);
    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/team/${editing.id}`, form);
      else await adminApi.post("/admin/team", form);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/team/${id}`);
    await load();
    toast.success("Deleted");
  };

  return (
    <PageContainer>
      <PageHeader title="Team" subtitle="Manage members shown on the public Team section."
        action={<Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="text-white" style={{ background: "#1B6FE0" }} data-testid="team-new"><Plus size={14} weight="bold"/> Add member</Button>} />

      <Card>
        <ul className="divide-y divide-slate-100" data-testid="team-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No members yet.</li>}
          {items.map((m) => (
            <li key={m.id} className="p-4 flex items-center gap-3" data-testid={`team-item-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14.5px]" style={{ color: "#0F2A6E" }}>{m.name}</div>
                <div className="text-[12px] text-[#6B7A8D]">{m.role} · order {m.order}</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEditing(m); setForm({ ...empty, ...m }); setOpen(true); }} data-testid={`team-edit-${m.id}`}><PencilSimple size={13} weight="bold"/></Button>
              <ConfirmDelete title="Delete this member?" description={`"${m.name}" will be removed from the public site.`} onConfirm={() => del(m.id)} testId={`team-del-${m.id}`}>
                <Button variant="outline" size="sm" className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]" data-testid={`team-del-${m.id}`}><Trash size={13} weight="bold"/></Button>
              </ConfirmDelete>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit member" : "Add member"}</DialogTitle>
            <DialogDescription>Members appear on the public Team section sorted by order.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="team-name"/></div>
              <div><Label>Role</Label><Input className="mt-1.5" value={form.role} onChange={(e) => set("role", e.target.value)} required/></div>
            </div>
            <div><Label>LinkedIn URL</Label><Input className="mt-1.5" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)}/></div>
            <ImageUpload value={form.photo_id} onChange={(id) => set("photo_id", id)} label="Photo" testId="team-photo"/>
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
              <Label className="ml-3">Order</Label>
              <Input type="number" className="w-24" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0"))}/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="team-save">{busy ? "Saving…" : (editing ? "Update" : "Add")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
